import { gql } from '@apollo/client';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { ConditionalLink } from 'src/components/conditionalLink/ConditionalLink';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { CheckWorkflowTaskStatus } from 'src/lib/graphqlTypes/types';

import { isWarningWorkflow } from '../../helpers';
import {
  getLinkToWorkflow,
  getLinkToWorkflowFragment,
} from '../../helpers/getLinkToWorkflow';

import { CheckWorkflowStatusIcon } from './CheckWorkflowStatusIcon';

export const DownstreamTaskCardFragment = gql`
  fragment DownstreamTaskCardFragment on DownstreamCheckResult {
    failsUpstreamWorkflow
    downstreamWorkflow {
      id
      status
      ...getLinkToWorkflowFragment
      baseVariant {
        id
        name
      }
      tasks {
        id
        status
        ... on FilterCheckTask {
          id
          buildResult {
            id
            ... on FilterBuildCheckFailed {
              errors {
                message
              }
            }
          }
        }
        ... on OperationsCheckTask {
          id
          result {
            id
            numberOfAffectedOperations
          }
        }
      }
    }
  }
  ${getLinkToWorkflowFragment}
`;

export function DownstreamTaskCard({
  downstreamWorkflow,
  failsUpstreamWorkflow,
}: {
  downstreamWorkflow: GraphQLTypes.DownstreamTaskCardFragment_downstreamWorkflow | null;
  failsUpstreamWorkflow: boolean | null;
}) {
  const location = useLocation();
  const internalGraphLinking = useInternalGraphLinking();

  if (downstreamWorkflow === null) return null;

  return (
    <div>
      <div className="flex items-center">
        {isWarningWorkflow(downstreamWorkflow, failsUpstreamWorkflow) ? (
          <CheckWorkflowStatusIcon className="size-4" status="WARNING" />
        ) : (
          <CheckWorkflowStatusIcon
            className="size-4"
            status={downstreamWorkflow.status}
          />
        )}
        <ConditionalLink
          className="ml-2 text-base text-link hover:underline"
          to={
            getLinkToWorkflow(
              downstreamWorkflow,
              location,
              internalGraphLinking,
            ) ?? undefined
          }
        >
          <b>{downstreamWorkflow.baseVariant?.name}</b>
        </ConditionalLink>
        {downstreamWorkflow.status ===
          GraphQLTypes.CheckWorkflowStatus.FAILED &&
          failsUpstreamWorkflow && (
            <div className="ml-2 rounded border border-silver-darker bg-white px-1.5 text-xs font-semibold tracking-wider text-black-lighter">
              BLOCKING
            </div>
          )}
      </div>

      <DownstreamWorkflowDescription workflow={downstreamWorkflow} />
    </div>
  );
}

function DownstreamWorkflowDescription({
  workflow,
}: {
  workflow: GraphQLTypes.DownstreamTaskCardFragment_downstreamWorkflow;
}) {
  const description = useMemo(() => {
    switch (workflow.status) {
      case GraphQLTypes.CheckWorkflowStatus.FAILED: {
        const filterCheckTaskResult = (
          workflow.tasks.find(
            (task) => task.__typename === 'FilterCheckTask',
          ) as GraphQLTypes.DownstreamTaskCardFragment_downstreamWorkflow_tasks_FilterCheckTask
        )?.buildResult;
        const buildErrorCount =
          (filterCheckTaskResult?.__typename === 'FilterBuildCheckFailed' &&
            filterCheckTaskResult?.errors.length) ??
          0;
        const operationCheckTask = workflow.tasks.find(
          (
            task,
          ): task is Extract<
            typeof task,
            { __typename: 'OperationsCheckTask' }
          > => task.__typename === 'OperationsCheckTask',
        );
        const affectedOperationCount =
          operationCheckTask?.result?.numberOfAffectedOperations;
        const failedOperationsCheck =
          operationCheckTask?.status === CheckWorkflowTaskStatus.FAILED;

        return buildErrorCount ? (
          <>
            This contract check workflow returned{' '}
            <span className="font-semibold text-error">
              {buildErrorCount} build{' '}
              {format.pluralize(buildErrorCount, 'error')}
            </span>{' '}
            and operations check was stalled.
          </>
        ) : affectedOperationCount ? (
          <>
            This contract check workflow returned{' '}
            <span className="font-semibold">
              {affectedOperationCount} affected{' '}
              {format.pluralize(affectedOperationCount, 'operation')}.
            </span>
          </>
        ) : failedOperationsCheck ? (
          `Potentially breaking changes detected.`
        ) : (
          `Build failed for unknown reason.`
        );
      }

      case GraphQLTypes.CheckWorkflowStatus.PASSED:
        return (
          <>
            All checks <span className="font-semibold">passed</span> for this
            contract check workflow.
          </>
        );

      case GraphQLTypes.CheckWorkflowStatus.PENDING:
        return 'Check in-progress...';

      default:
        return assertUnreachable(workflow.status);
    }
  }, [workflow.tasks, workflow.status]);

  return <div className="ml-6 text-sm text-secondary">{description}</div>;
}
